import type { ReactNode } from 'react';
import { Alert, Button, Code, Group, List, Stack, Text, type MantineSpacing } from '@mantine/core';
import { IconAlertCircle, IconWifiOff } from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { ApiError, type Blocker } from '@web/lib/api';

/** Human message for any thrown value (ApiError detail is already localised by the server). */
export function problemMessage(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    if (error.isNetworkError) return t('common:errors.network');
    if (error.detail) return error.detail;
    if (error.status === 403) return t('common:errors.forbiddenText');
    if (error.status === 404) return t('common:errors.notFoundText');
    return error.title || t('common:errors.generic');
  }
  if (error instanceof Error) return error.message;
  return t('common:errors.generic');
}

export function blockerMessage(b: Blocker, lang: string): string {
  return lang === 'en' ? b.messageEn || b.messageAr : b.messageAr || b.messageEn;
}

/**
 * Copies server field errors (`errors[{ path, message }]`) into react-hook-form.
 * Returns true when at least one error was mapped.
 */
export function applyServerErrors<T extends FieldValues>(error: unknown, setError: UseFormSetError<T>): boolean {
  if (!(error instanceof ApiError) || error.errors.length === 0) return false;
  for (const fe of error.errors) setError(fe.path as Path<T>, { type: 'server', message: fe.message });
  return true;
}

export interface ProblemAlertProps {
  error: unknown;
  title?: ReactNode;
  /** e.g. reload the record after a version conflict */
  onRetry?: () => void;
  retryLabel?: string;
  onClose?: () => void;
  /** labels for `errors[].path` (defaults to the raw path) */
  fieldLabels?: Record<string, string>;
  mb?: MantineSpacing;
  mt?: MantineSpacing;
}

/**
 * Renders an {@link ApiError}: localised detail, gate `details.blockers`, field errors,
 * version-conflict hint and the request id (for support).
 * @example {mutation.error && <ProblemAlert error={mutation.error} onRetry={refetch} />}
 */
export function ProblemAlert({ error, title, onRetry, retryLabel, onClose, fieldLabels, mb, mt }: ProblemAlertProps) {
  const { t, i18n } = useTranslation();
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  const blockers = api?.blockers ?? [];
  const fieldErrors = api?.errors ?? [];
  const network = api?.isNetworkError ?? false;
  return (
    <Alert
      color={network ? 'orange' : 'red'}
      variant="light"
      icon={network ? <IconWifiOff aria-hidden /> : <IconAlertCircle aria-hidden />}
      title={title ?? (blockers.length ? t('common:errors.blockedTitle') : t('common:errors.title'))}
      withCloseButton={!!onClose}
      closeButtonLabel={t('common:actions.close')}
      onClose={onClose}
      mb={mb}
      mt={mt}
      role="alert"
    >
      <Stack gap={6}>
        <Text size="sm">{problemMessage(error, t)}</Text>
        {blockers.length > 0 && (
          <List size="sm" spacing={4} aria-label={t('common:errors.blockers')}>
            {blockers.map((b, i) => (
              <List.Item key={`${b.code}-${i}`}>
                {blockerMessage(b, i18n.language)}{' '}
                <Code fz="xs" dir="ltr">
                  {b.code}
                </Code>
              </List.Item>
            ))}
          </List>
        )}
        {fieldErrors.length > 0 && (
          <List size="sm" spacing={2} aria-label={t('common:errors.fieldErrors')}>
            {fieldErrors.map((fe, i) => (
              <List.Item key={`${fe.path}-${i}`}>
                <Text span fw={600} size="sm">
                  {fieldLabels?.[fe.path] ?? fe.path}
                </Text>
                : {fe.message}
              </List.Item>
            ))}
          </List>
        )}
        {api?.isVersionConflict && (
          <Text size="sm" c="dimmed">
            {t('common:errors.versionConflictHint')}
          </Text>
        )}
        <Group justify="space-between" gap="xs">
          {api && !network ? (
            <Text size="xs" c="dimmed" dir="ltr">
              {api.code}
              {api.requestId ? ` · ${api.requestId}` : ''}
            </Text>
          ) : (
            <span />
          )}
          {onRetry && (
            <Button size="compact-sm" variant="light" color={network ? 'orange' : 'red'} onClick={onRetry}>
              {retryLabel ?? (api?.isVersionConflict ? t('common:actions.reload') : t('common:actions.retry'))}
            </Button>
          )}
        </Group>
      </Stack>
    </Alert>
  );
}
